import { Box, Stack } from "@mui/material";

import { GqlPlannerV2VisitPossibleDaysStatus } from "~/__generated__";
import { formatDate } from "~/modules/app/util/formatters";

import { TimelineDateLabels } from "./components/TimelineDateLabels";
import { TimelineDay } from "./components/TimelineDay";
import { TimelineMessage } from "./components/TimelineMessage";
import { TimelineStatusMarker } from "./components/TimelineStatusMarker";
import { VisitStatusTimelineFragment } from "./fragment.generated";
import { useTimelineMarker, useTimelineRange } from "./hooks";

type VisitStatusTimelineProps = {
    selectedDate: Date;
    onClick?: (date: Date) => void;
    plannerStatus: VisitStatusTimelineFragment["plannerStatus"];
    possibleDays: VisitStatusTimelineFragment["possibleDays"];
    variant: "full" | "minimal";
};

export function VisitStatusTimeline({
    onClick,
    possibleDays,
    plannerStatus,
    selectedDate,
    variant = "full",
}: VisitStatusTimelineProps) {
    const timelineRange = useTimelineRange({ possibleDays });
    const markerData = useTimelineMarker({ timelineRange, markerDate: selectedDate, possibleDays });

    if (!timelineRange || !markerData) return null;

    const { firstStartDate, latestEndDate, firstStartDayIndex, latestEndDayIndex, dayData } = timelineRange;
    const { markerDayIndex, markerStatus, markerPositionPercent } = markerData;

    if (plannerStatus === GqlPlannerV2VisitPossibleDaysStatus.Planned) {
        return null;
    }

    return (
        <Stack rowGap={2}>
            {variant === "full" && (
                <TimelineMessage plannerStatus={plannerStatus} firstStartDate={firstStartDate} latestEndDate={latestEndDate} />
            )}
            <Box
                sx={{
                    flexGrow: 1,
                    display: "grid",
                    gridTemplateColumns: `repeat(${dayData.length}, minmax(2px, 1fr))`,
                    gridTemplateRows: `auto ${variant === "full" ? 16 : 8}px auto`,
                    position: "relative",
                    containerType: "inline-size",
                }}
            >
                <TimelineStatusMarker
                    status={markerStatus}
                    markerDayIndex={markerDayIndex}
                    markerPositionPercent={markerPositionPercent}
                    variant={variant}
                />

                {dayData.map(({ day, index, status }) => (
                    <TimelineDay key={formatDate(day)} day={day} onClick={onClick} index={index} status={status} />
                ))}

                {variant === "full" && (
                    <TimelineDateLabels
                        firstStartDate={firstStartDate}
                        latestEndDate={latestEndDate}
                        firstStartDayIndex={firstStartDayIndex}
                        latestEndDayIndex={latestEndDayIndex}
                    />
                )}
            </Box>
        </Stack>
    );
}
